import { RendererType } from '../../../../data-models/field-definition';
import { IUpdateCustomFieldDataPayload } from '../../../../services/queries/MaggieCustomFieldQueries';
import { isDateOrDateString, plainDateFromDateOrDateString } from '../../../../util/date-utilts';
import { CustomFieldDataGridData } from './TimeSeriesDataTable';

// assumes valid row data

export function timeSeriesRowDataToCustomFieldData(
  rowData: CustomFieldDataGridData[]
): IUpdateCustomFieldDataPayload[] {
  return rowData.map((row) => {
    return {
      id: row.id,
      customFieldId: row.customFieldId,
      entityId: row.entityId,
      value: row.value ?? null,
      date: plainDateFromDateOrDateString(row.startDate),
    };
  });
}
export const duplicateDateMessage = 'Data cannot contain duplicate dates.';
export const missingDateMessage = 'Please fill in all dates.';
type IValidationResult =
  | { isValid: true }
  | { isValid: false; updatedRowData: CustomFieldDataGridData[]; errorMessage: string };

export function validateTimeSeriesData(rowData: CustomFieldDataGridData[]): IValidationResult {
  const errors: number[] = [];
  const duplicateDates = checkForDuplicateDates(rowData);
  let errorMessage = '';
  if (duplicateDates.length) {
    errorMessage = duplicateDateMessage;
  }
  errors.push(...duplicateDates);
  const missingDates = checkForMissingDates(rowData);
  if (missingDates.length) {
    errorMessage = `${missingDateMessage}\n${errorMessage}`;
  }
  errors.push(...missingDates);
  const errorSet = new Set(errors);
  if (errorSet.size > 0) {
    return {
      isValid: false,
      updatedRowData: sortGridData(rowData.map((row) => ({ ...row, error: errorSet.has(row.id) }))),
      errorMessage,
    };
  }
  return { isValid: true };
}
function checkForDuplicateDates(rowData: CustomFieldDataGridData[]) {
  const dates = new Map<string, number[]>();
  for (const row of rowData) {
    if (!row.startDate) {
      continue;
    }
    const dateString = plainDateFromDateOrDateString(row.startDate);
    if (dates.has(dateString)) {
      dates.get(dateString)!.push(row.id);
    } else {
      dates.set(dateString, [row.id]);
    }
  }
  return Array.from(dates.values())
    .filter((ids) => ids.length > 1)
    .flat();
}
function checkForMissingDates(rowData: CustomFieldDataGridData[]) {
  const result: number[] = [];
  for (const row of rowData) {
    if (!isDateOrDateString(row.startDate, RendererType.date)) {
      result.push(row.id);
    }
  }
  return result;
}
function sortGridData(data: CustomFieldDataGridData[]) {
  return data.toSorted((a, b) =>
    // startDate is user-entered, so might be missing or invalid - fallback to date
    // so that empty values are not moved to top or bottom
    plainDateFromDateOrDateString(
      isDateOrDateString(b.startDate, RendererType.date) ? b.startDate : b.date
    ).localeCompare(
      plainDateFromDateOrDateString(isDateOrDateString(a.startDate, RendererType.date) ? a.startDate : a.date)
    )
  );
}
